/**
 * My Assessments page — shows tasks assigned to the current user
 */

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { penilaianApi } from '../api/penilaianApi'

const ROLE_LABELS: Record<string, string> = {
  ATASAN: 'Sebagai Atasan',
  BAWAHAN: 'Sebagai Bawahan',
  PEER: 'Sebagai Rekan Kerja',
  KOMITE: 'Sebagai Komite',
}

const STATUS_STYLES = {
  pending: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-yellow-100 text-yellow-700',
  submitted: 'bg-green-100 text-green-700',
}

const STATUS_LABELS = {
  pending: 'Belum Dimulai',
  in_progress: 'Sedang Dikerjakan',
  submitted: 'Sudah Dikumpulkan',
}

export default function MyAssessmentsPage() {
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('')

  const { data: periodsData } = useQuery({
    queryKey: ['penilaian-periods'],
    queryFn: () => penilaianApi.getPeriods(),
  })

  const periods = periodsData?.data ?? []
  const selectedPeriod = periods.find((p) => p.id === selectedPeriodId)

  // Auto-select the open period, or the most recent period if none is open
  useEffect(() => {
    if (selectedPeriodId || periods.length === 0) return
    const open = periods.find((p) => p.status === 'open')
    if (open) { setSelectedPeriodId(open.id); return }
    const sorted = [...periods].sort((a, b) =>
      b.year !== a.year ? b.year - a.year : b.quarter - a.quarter
    )
    if (sorted[0]) setSelectedPeriodId(sorted[0].id)
  }, [periods, selectedPeriodId])

  const { data: assignmentsData, isLoading } = useQuery({
    queryKey: ['my-penilaian-assignments', selectedPeriodId],
    queryFn: () => penilaianApi.getMyAssignments(selectedPeriodId),
    enabled: !!selectedPeriodId,
  })

  const assignments = assignmentsData?.data ?? []

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Tugas Penilaian Saya</h1>
        <p className="text-sm text-gray-500 mt-1">
          Daftar penilaian yang perlu Anda isi untuk periode aktif
        </p>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Pilih Periode</label>
        <select
          value={selectedPeriodId}
          onChange={(e) => setSelectedPeriodId(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-72"
        >
          <option value="">-- Pilih periode --</option>
          {periods.length === 0 && (
            <option value="" disabled>Tidak ada periode</option>
          )}
          {periods.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} {p.status === 'open' ? '(Aktif)' : p.status === 'draft' ? '(Draft)' : '(Ditutup)'}
            </option>
          ))}
        </select>
      </div>

      {/* Period status notice */}
      {selectedPeriod && selectedPeriod.status === 'draft' && (
        <div className="mb-4 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          Periode ini masih <strong>Draft</strong> — Anda bisa melihat tugas yang sudah di-assign, tapi belum bisa mengisi formulir sampai admin membuka periode.
        </div>
      )}
      {selectedPeriod && selectedPeriod.status === 'closed' && (
        <div className="mb-4 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
          Periode ini sudah <strong>Ditutup</strong> — pengisian formulir sudah tidak bisa dilakukan.
        </div>
      )}

      {!selectedPeriodId && (
        <div className="text-center py-12 text-gray-400">Pilih periode untuk melihat tugas Anda</div>
      )}

      {selectedPeriodId && isLoading && (
        <div className="text-center py-12 text-gray-500">Memuat...</div>
      )}

      {selectedPeriodId && !isLoading && assignments.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <p>Tidak ada tugas penilaian untuk periode ini.</p>
          <p className="text-xs mt-1">Pastikan admin sudah assign Anda sebagai penilai di halaman Penugasan.</p>
        </div>
      )}

      {assignments.length > 0 && (
        <div className="space-y-3">
          {assignments.map((assignment) => (
            <div
              key={assignment.id}
              className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between"
            >
              <div>
                <div className="font-medium text-gray-900">
                  {assignment.assessee?.display_name ?? assignment.assessee?.email}
                </div>
                <div className="text-sm text-gray-500 mt-0.5">
                  {assignment.assessee?.job_title ?? ''}{' '}
                  {assignment.assessee?.department ? `· ${assignment.assessee.department}` : ''}
                </div>
                <div className="mt-2">
                  <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                    {ROLE_LABELS[assignment.assessor_role]}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span
                  className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_STYLES[assignment.status]}`}
                >
                  {STATUS_LABELS[assignment.status]}
                </span>
                {selectedPeriod?.status === 'closed' ? (
                  assignment.status === 'submitted' ? (
                    <Link
                      to={`/penilaian/assess/${assignment.id}`}
                      className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
                    >
                      Lihat
                    </Link>
                  ) : null
                ) : (
                  <>
                    {assignment.status !== 'submitted' && (
                      <Link
                        to={`/penilaian/assess/${assignment.id}`}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                      >
                        {assignment.status === 'in_progress' ? 'Lanjutkan' : 'Mulai Penilaian'}
                      </Link>
                    )}
                    {assignment.status === 'submitted' && (
                      <Link
                        to={`/penilaian/assess/${assignment.id}`}
                        className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
                      >
                        Lihat
                      </Link>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
